// time-selector.js

import find from "lodash/find";
import get from "lodash/get";
import { parse } from "query-string";
import {
  withoutFocus,
  removeQueryParams,
  updateLocation,
} from "../helpers/router";
import { createSelector } from "reselect";
import { toSeconds } from "apm/helpers/time";

const MINUTE = 60; // 1 minute in sec
const HOUR = 60 * 60; // 1 hour in sec
const SIXTY_MINUTES = "60 minutes";
const DAY = 24 * HOUR;

export const OPTIONS = [
  { label: "30 minutes", interval: 30 * MINUTE },
  { label: SIXTY_MINUTES, interval: 60 * MINUTE },
  { label: "3 hours", interval: 3 * HOUR },
  { label: "6 hours", interval: 6 * HOUR },
  { label: "24 hours", interval: DAY },
  { label: "3 days", interval: 3 * DAY },
  { label: "7 days", interval: 7 * DAY },
];

export const FORMATS = {
  local: "MMM Do, YYYY h:mm A",
  utc: "MMM Do, YYYY H:mm UTC",
};

// Eventually we probably want to save/check for a localstorage state so this
// can be a "sticky" setting
let utc;
try {
  utc = Session.settings.utc;
} catch (e) {
  utc = false;
}

export const INITIAL_STATE = {
  now: Date.now(),
  isCustomRangeActive: false,
  isNowActive: true,
  utc,
};

// action types
const SET_TIME = "trace-web/time-selector/SET_TIME";
const TOGGLE_CUSTOM = "trace-web/time-selector/TOGGLE_CUSTOM";
const TOGGLE_NOW = "trace-web/time-selector/TOGGLE_NOW";
const TOGGLE_UTC = "trace-web/time-selector/TOGGLE_UTC";

const floorSeconds = date => new Date(date.setSeconds(0));

// reducer
const timeSelectorReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_TIME:
      return {
        ...state,
        now: Date.now(),
      };

    case TOGGLE_CUSTOM:
      return {
        ...state,
        isCustomRangeActive: action.value,
      };

    case TOGGLE_NOW:
      return {
        ...state,
        isNowActive: action.value,
      };

    case TOGGLE_UTC:
      return {
        ...state,
        utc: action.value,
      };

    default:
      return state;
  }
};
export default timeSelectorReducer;

// action creators
export const setTime = () => {
  return {
    type: SET_TIME,
  };
};

export const setCustomRangeActive = value => {
  return {
    type: TOGGLE_CUSTOM,
    value,
  };
};

export const setNowActive = value => {
  return {
    type: TOGGLE_NOW,
    value,
  };
};

export const setUtcActive = value => {
  return {
    type: TOGGLE_UTC,
    value,
  };
};

// utility functions
export const goToTime = (history, location, duration, endTime) => {
  if (history.push === undefined) {
    return;
  }

  let loc = withoutFocus(location);
  let search = {
    duration: duration,
  };

  if (endTime) {
    search.end_time = endTime;
  } else {
    loc = removeQueryParams(loc, ["end_time"]);
  }

  const to = updateLocation(loc, null, search);
  history.push(to);
};

export const updateTimeSelection = (
  currentDuration,
  urlDuration,
  endTime,
  location,
  history
) => {
  if (currentDuration === 0 || urlDuration !== 0) {
    return;
  }
  const selectedTimeObject = {
    duration: currentDuration,
  };

  if (endTime) {
    selectedTimeObject.end_time = endTime.toString();
  } else {
    removeQueryParams(location, ["end_time"]);
  }

  const nextLocation = updateLocation(location, undefined, selectedTimeObject);
  history.replace(nextLocation);
};

// selectors
//
// this first set should not change unless the url or store change
const getSearchString = (state, props) => get(state, "routing.location.search");
const getSearch = createSelector([getSearchString], search => parse(search));
const getDurationString = createSelector(
  [getSearch],
  search => search.duration
);
export const getEndTimeString = createSelector(
  [getSearch],
  search => search.end_time
);
const getNow = state => state.ui.timeSelector.now;
const getUtcValue = state => state.ui.timeSelector.utc;
const getIsCustomerRangeActive = (state, props) =>
  state.ui.timeSelector.isCustomRangeActive;
const getCustomRangeActive = createSelector(
  [getIsCustomerRangeActive, getEndTimeString],
  (isCustomRangeActive, endTimeString) => isCustomRangeActive || !!endTimeString
);
const getNowActive = state => state.ui.timeSelector.isNowActive;
export const getTimeSelectorOpen = state =>
  state.ui.dropdowns.isTimeSelectorOpen;

// these should be memoized
export const getDuration = createSelector(
  [getDurationString],
  durationString => (durationString ? Number(durationString) : HOUR)
);

export const getDurationInSeconds = createSelector(
  [getDurationString],
  duration => parseInt(duration) || 0
);

export const getEndTimeAsEpoch = createSelector(
  [getEndTimeString],
  endTime => parseInt(endTime) || 0
);

export const getUtc = createSelector([getUtcValue], utc => utc);

export const getEndTimeStamp = createSelector(
  [getEndTimeString, getNow],
  (endTimeString, now) => {
    const endTimeStamp = endTimeString ? Number(endTimeString) : toSeconds(now);
    return endTimeStamp;
  }
);

export const getEndTime = createSelector(
  [getEndTimeString, getNow],
  (endTimeString, now) => {
    const endTimeStamp = endTimeString ? Number(endTimeString) * 1000 : now;
    return new Date(endTimeStamp);
  }
);

export const getStartTimeStamp = createSelector(
  [getDuration, getEndTimeStamp],
  (duration, endTime) => endTime - duration
);

export const getStartTime = createSelector(
  [getDuration, getEndTime],
  (duration, endTime) => new Date(endTime - duration * 1000)
);

export const getLabel = createSelector(
  [getDuration, getEndTimeString],
  (duration, endTimeString) => {
    if (endTimeString) return;
    const entry = find(OPTIONS, o => o.interval === duration);
    return entry ? entry.label : entry;
  }
);

export const getDefaultDurationInSeconds = data => {
  return get(
    data,
    "viewer.authenticatedUser.preferences.defaultDurationInSeconds",
    0
  );
};

export const getTime = createSelector(
  [
    getDuration,
    getEndTime,
    getStartTime,
    getLabel,
    getUtc,
    getCustomRangeActive,
    getNowActive,
  ],
  (
    duration,
    endTime,
    startTime,
    label,
    utc,
    isCustomRangeActive,
    isNowActive
  ) => {
    return {
      interval: duration,
      end: endTime,
      start: startTime,
      label,
      utc,
      isCustomRangeActive,
      isNowActive,
    };
  }
);

export const getTimeRangeOverride = (_, props) => {
  return {
    start:
      props.timeRangeOverride && props.timeRangeOverride.start
        ? new Date(props.timeRangeOverride.start)
        : null,
    end:
      props.timeRangeOverride && props.timeRangeOverride.end
        ? new Date(props.timeRangeOverride.end)
        : null,
  };
};

export const getTimeFloored = createSelector(
  [getTime, getTimeRangeOverride],
  (time, timeRangeOverride) => {
    return {
      ...time,
      start: floorSeconds(timeRangeOverride.start || time.start),
      end: floorSeconds(timeRangeOverride.end || time.end),
    };
  }
);
